import { DotmTriangle2 } from "@/components/ui/dotm-triangle-2";
import { cn } from "@/lib/utils";

// Brand loader. Wraps DotmTriangle2 and binds it to our brand token via
// text-primary (DotmTriangle2's default `color="currentColor"` picks it up).
//
// One component, three sizes, all loading states across the LMS go through
// here. Replace `<Loader2 className="animate-spin" />`, "Loading..." text,
// and any other ad-hoc spinner with this.
//
// Usage:
//   <Loader />              // default (sm) — inside buttons, inline spots
//   <Loader size="md" />    // panels, list states
//   <Loader size="lg" />    // full-page route loaders
//   <Loader label="Loading courses" />  // text below the matrix, centered

const SIZE_PX = { xs: 14, sm: 20, md: 32, lg: 48 };
const DOT_PX  = { xs: 2,  sm: 3.5, md: 6,  lg: 8 };

export function Loader({
  size = "sm",
  label,
  className,
  tone = "primary",
  muted = false,
  ariaLabel,
  ...rest
}) {
  const px = SIZE_PX[size] ?? SIZE_PX.sm;
  const dot = DOT_PX[size] ?? DOT_PX.sm;
  const toneClass =
    tone === "muted" ? "text-muted-foreground" :
    tone === "current" ? "" :
    "text-primary";

  if (label) {
    return (
      <div
        className={cn("inline-flex flex-col items-center gap-2", toneClass, className)}
        {...rest}
      >
        <DotmTriangle2
          size={px}
          dotSize={dot}
          ariaLabel={ariaLabel ?? label}
          muted={muted}
        />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
    );
  }

  return (
    <DotmTriangle2
      size={px}
      dotSize={dot}
      ariaLabel={ariaLabel ?? "Loading"}
      muted={muted}
      className={cn(toneClass, className)}
      {...rest}
    />
  );
}

// Centered full-area variant for empty/loading states inside cards or
// route shells. Stretches to fill its parent and centers the loader.
export function LoaderArea({ size = "md", label, minHeight = 200, className }) {
  return (
    <div
      className={cn("flex items-center justify-center w-full", className)}
      style={{ minHeight }}
    >
      <Loader size={size} label={label} />
    </div>
  );
}

export default Loader;
